import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AbilityService } from 'ability';
import { isNil } from 'lodash';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import {
  CblsesDomainVerificationResponse,
  EmailServiceStatus,
  GetBouncedListRequestParams,
  GetBouncedListResponse,
  NotificationGeneralProvider,
  NotificationMethod
} from 'mbs-ui-shared@models/Notification';
import { objectToParams } from 'mbs-ui-shared@utils/object-to-params';
import { Observable, distinctUntilChanged, filter, map, of, shareReplay } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class NotificationServerSettingsService {
  private http = inject(HttpClient);
  private auth = inject(AuthFacade);
  private ability = inject(AbilityService);

  private emailStatus$: Observable<EmailServiceStatus>;

  get(): Observable<NotificationGeneralProvider> {
    return this.http.get<NotificationGeneralProvider>('api/email-service-settings');
  }

  save(settings: NotificationGeneralProvider): Observable<NotificationGeneralProvider> {
    return this.http.put<NotificationGeneralProvider>(`api/email-service-settings/${NotificationMethod[settings.type]}`, settings);
  }

  verifiedEmail(settings: NotificationGeneralProvider): Observable<NotificationGeneralProvider> {
    return this.http.post<NotificationGeneralProvider>(`api/email-service-settings/${NotificationMethod[settings.type]}/verify-email`, {
      EmailFrom: settings.emailFrom,
      s3AccountId: settings.sesSettings ? settings.sesSettings.account : null
    });
  }

  checkVerifiedEmail(settings: NotificationGeneralProvider): Observable<boolean> {
    return this.http.get<boolean>(`api/email-service-settings/${NotificationMethod[settings.type]}/email-verified`, {
      params: {
        emailFrom: settings.emailFrom,
        s3AccountId: settings.type === NotificationMethod.SES ? settings.sesSettings.account : null
      }
    });
  }

  verifyDomain(params: { emailFrom: string; needRetry: boolean }): Observable<CblsesDomainVerificationResponse> {
    return this.http.post<CblsesDomainVerificationResponse>('api/email-service-settings/cblses/verify-domain', params);
  }

  checkSESFullAccess(accountId: string): Observable<boolean> {
    return this.http.get<boolean>(`api/email-service-settings/ses/account/${accountId}/is-production-access`);
  }

  sendTestEmail(emailTo: string, settings: NotificationGeneralProvider): Observable<boolean> {
    return this.http.post<boolean>(`api/email-service-settings/${NotificationMethod[settings.type]}/send-test-email`, {
      emailTo,
      settings
    });
  }

  checkStatus(settings: NotificationGeneralProvider): Observable<NotificationGeneralProvider> {
    return this.http.get<NotificationGeneralProvider>(`api/email-service-settings/${NotificationMethod[settings.type]}/check-status`, {
      params: {
        emailFrom: settings.emailFrom,
        s3AccountId: settings.type === NotificationMethod.SES ? settings.sesSettings.account : null
      }
    });
  }

  checkEmailServiceConfigured(force = false): Observable<EmailServiceStatus> {
    if (!this.emailStatus$ || force) {
      this.emailStatus$ = this.auth.currentUser$.pipe(
        switchMap((user) => {
          const skipRequest = !user?.IsProvider && this.ability.cannot('read', 'Notification');
          return skipRequest ? of(null) : this.http.get<{ status: EmailServiceStatus }>('api/email-service/status');
        }),
        map((result) => result?.status),
        shareReplay(1)
      );
    }

    return this.emailStatus$.pipe(
      filter((value) => !isNil(value)),
      distinctUntilChanged()
    );
  }

  removeFromBounceList(email: string) {
    return this.http.delete(`api/sesreport?email=${email}`);
  }

  getBounceListLength() {
    return this.http.get<number>('api/sesreport/count');
  }

  getBounceList(params?: GetBouncedListRequestParams): Observable<GetBouncedListResponse> {
    return this.http.get<GetBouncedListResponse>('api/sesreport', {
      params: objectToParams({ ...params })
    });
  }
}
